import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, LogOut, CheckCircle, XCircle, FileText, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import Logo from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const [runners, setRunners] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/verifications?status_filter=${filter}`, { credentials: 'include' });
      if (res.ok) setRunners(await res.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [filter]);

  const decide = async (runnerId, approve, reason = null) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/verifications/${runnerId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ approve, rejection_reason: reason })
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(approve ? 'Runner approved!' : 'Runner rejected');
      setSelected(null);
      setRejecting(false);
      setRejectReason('');
      fetchList();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleLogout = async () => {
    await fetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    navigate('/login');
  };

  const filters = [
    { id: 'pending', label: 'Pending', icon: Clock },
    { id: 'verified', label: 'Verified', icon: CheckCircle },
    { id: 'rejected', label: 'Rejected', icon: XCircle },
    { id: 'all', label: 'All', icon: FileText }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Logo size="md" />
            <span className="px-2 py-0.5 bg-slate-900 text-yellow-400 text-xs font-bold rounded">ADMIN</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-slate-700 font-medium hidden md:block">{user?.name}</span>
            <Button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-full px-4 py-2 font-medium btn-hover flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" /> <span className="hidden md:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 lg:px-12 py-8">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Verification Reviews</h2>
          <p className="text-slate-600">Approve or reject runner document submissions</p>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {filters.map((f) => (
            <button
              key={f.id}
              data-testid={`filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-all ${
                filter === f.id
                  ? 'bg-slate-900 text-white'
                  : 'bg-white text-slate-700 border border-slate-200 hover:border-slate-400'
              }`}
            >
              <f.icon className="w-4 h-4" /> {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900 mx-auto"></div></div>
        ) : runners.length === 0 ? (
          <Card className="p-12 text-center">
            <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">No runners with status "{filter}"</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {runners.map((r) => (
              <Card key={r.user_id} data-testid={`runner-card-${r.user_id}`} className="p-5 hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelected(r)}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-slate-900">{r.name}</p>
                    <p className="text-sm text-slate-600">{r.email}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    r.verification_status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                    r.verification_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    r.verification_status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {r.verification_status || 'unverified'}
                  </span>
                </div>
                <div className="flex gap-2 text-xs text-slate-500">
                  <span>{r.id_document_path ? '✓' : '✗'} ID</span>
                  <span>{r.selfie_path ? '✓' : '✗'} Selfie</span>
                  <span>{r.address_proof_path ? '✓' : '✗'} Address</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="review-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">{selected?.name}</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 mt-4">
              <p className="text-slate-600 text-sm">{selected.email}</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label: 'ID Copy', path: selected.id_document_path },
                  { label: 'Selfie', path: selected.selfie_path },
                  { label: 'Proof of Address', path: selected.address_proof_path }
                ].map((doc) => (
                  <div key={doc.label} className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-slate-700 mb-2">{doc.label}</p>
                    {doc.path ? (
                      <img
                        data-testid={`doc-preview-${doc.label.toLowerCase().replace(/ /g, '-')}`}
                        src={`${BACKEND_URL}/api/files/${doc.path}`}
                        alt={doc.label}
                        className="w-full h-48 object-cover rounded border border-slate-200 bg-white"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                      />
                    ) : null}
                    {!doc.path && <p className="text-xs text-slate-400 italic">Not uploaded</p>}
                    {doc.path && (
                      <a href={`${BACKEND_URL}/api/files/${doc.path}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline mt-1 block">
                        Open full size
                      </a>
                    )}
                  </div>
                ))}
              </div>

              {selected.verification_status === 'pending' && !rejecting && (
                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <Button data-testid="reject-btn" onClick={() => setRejecting(true)} className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 rounded-full py-3 font-medium border border-red-200">
                    Reject
                  </Button>
                  <Button data-testid="approve-btn" onClick={() => decide(selected.user_id, true)} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-3 font-bold btn-hover">
                    Approve
                  </Button>
                </div>
              )}

              {rejecting && (
                <div className="pt-4 border-t border-slate-200 space-y-3">
                  <Textarea
                    data-testid="reject-reason-input"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Reason for rejection (shown to runner)"
                    className="bg-slate-50 border-slate-200 rounded-lg"
                    rows={3}
                  />
                  <div className="flex gap-2">
                    <Button onClick={() => { setRejecting(false); setRejectReason(''); }} className="flex-1 bg-slate-100 text-slate-900 rounded-full py-2">Cancel</Button>
                    <Button
                      data-testid="confirm-reject-btn"
                      onClick={() => decide(selected.user_id, false, rejectReason)}
                      disabled={!rejectReason.trim()}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-full py-2 font-bold"
                    >
                      Confirm Rejection
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
